import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import useApi from '@/hooks/useApi';

import { getListPurchaseOptionsResponse } from '@/services/requests/ListPurchaseOptions/mapper';
import { TResponseListPurchaseOptionsData } from '@/services/requests/ListPurchaseOptions/types';
import * as voucherServiceMapper from '@/services/requests/Voucher/mapper';
import { TVoucherItem } from '@/services/requests/Voucher/types';

export type TVoucherList = {
  all: TVoucherItem[];
  milesPurchase: TVoucherItem[];
  airline: TVoucherItem[];
};

export type TPurchaseContext = {
  voucherList: {
    all: TVoucherItem[];
    milesPurchase: TVoucherItem[];
    airline: TVoucherItem[];
  };
  setVoucherList: Dispatch<SetStateAction<TVoucherList>>;
  currentVoucher?: TVoucherItem;
  fetchVoucherList: () => void;
  setChangeVoucher: (voucherNumber: string) => void;
  listMilesPurchase: TResponseListPurchaseOptionsData['listMilesPurchase'];
  getPurchaseValues: (inputValue: number) => {
    installments: number;
    total: number;
    installmentValue: number;
    totalWithoutDiscount: number;
    id: string;
    mileValueWithDiscount: number;
    mileValue: number;
    expirationDate: string;
  };
  milesToPurchase: number;
  setMilesToPurchase: (value: number) => void;
  getListQuantityMilesToPurchase: () => number[];
};

export type TPurchaseValues = {
  installments: number;
  total: number;
  installmentValue: number;
  totalWithoutDiscount: number;
  id: string;
  mileValueWithDiscount: number;
  mileValue: number;
  expirationDate: string;
};

export const PurchaseContext = createContext({} as TPurchaseContext);

const PurchaseProvider: React.FC = ({ children }) => {
  const [voucherList, setVoucherList] = useState<TVoucherList>({
    all: [],
    milesPurchase: [],
    airline: [],
  });
  const [currentVoucher, setCurrentVoucher] = useState<TVoucherItem>();
  const [listMilesPurchase, setListMilesPurchase] = useState<
    TResponseListPurchaseOptionsData['listMilesPurchase']
  >([]);
  const [milesToPurchase, setMilesToPurchase] = useState<number>(0);

  const { ApiGetVoucherList, ApiGetListPurchaseOptions } = useApi();

  const handleChangeVoucher = useCallback(
    (voucherNumber: string) => {
      setCurrentVoucher(
        voucherList.all.find(
          voucher => voucher.voucherNumber === voucherNumber,
        ) || voucherList.all[0],
      );
    },
    [voucherList],
  );

  const fetchVoucherList = useCallback(async () => {
    const result = await voucherServiceMapper.getVoucherListResponse(
      ApiGetVoucherList,
    );
    setVoucherList({
      all: result.voucherMemberList,
      milesPurchase: voucherServiceMapper.filterMilesPurchaseVouchers(
        result.voucherMemberList,
      ),
      airline: voucherServiceMapper.filterAirlineVouchers(
        result.voucherMemberList,
      ),
    });
  }, [ApiGetVoucherList]);

  const fetchListPurchaseOptionsResponse = useCallback(
    async (voucherNumber: string) => {
      const response = await getListPurchaseOptionsResponse(
        ApiGetListPurchaseOptions,
        voucherNumber,
      );
      setListMilesPurchase(response.listMilesPurchase);
    },
    [ApiGetListPurchaseOptions],
  );

  const getPurchaseValues = useCallback(
    (inputValue: number): TPurchaseValues => {
      if (currentVoucher && listMilesPurchase.length) {
        const {
          milesPrice,
          quantityMiles,
          installments: maxInstallments,
          id,
          expirationDate,
        } = listMilesPurchase.find(item => item.quantityMiles === inputValue) ||
        listMilesPurchase[listMilesPurchase.length - 1];

        const percent = currentVoucher.discount / 100;
        const mileValue = milesPrice / quantityMiles;
        const mileValueWithDiscount = mileValue * (1 - percent);
        const total = Number(mileValueWithDiscount.toFixed(4)) * inputValue;
        const minInstallmentValue = 5;
        let installments = Math.floor(total / minInstallmentValue);

        installments =
          installments <= maxInstallments ? installments : maxInstallments;

        const installmentValue = total / installments;

        const totalWithoutDiscount = mileValue * inputValue;

        return {
          installments,
          total,
          installmentValue,
          totalWithoutDiscount,
          id,
          mileValueWithDiscount,
          mileValue,
          expirationDate,
        };
      }
      return {
        installments: 0,
        total: 0,
        installmentValue: 0,
        totalWithoutDiscount: 0,
        id: '',
        mileValueWithDiscount: 0,
        mileValue: 0,
        expirationDate: '',
      };
    },
    [currentVoucher, listMilesPurchase],
  );

  const getListQuantityMilesToPurchase = useCallback((): number[] => {
    return listMilesPurchase.map(item => item.quantityMiles);
  }, [listMilesPurchase]);

  useEffect(() => {
    if (currentVoucher) {
      fetchListPurchaseOptionsResponse(currentVoucher.voucherNumber);
    }
  }, [currentVoucher, fetchListPurchaseOptionsResponse]);

  return (
    <PurchaseContext.Provider
      value={{
        voucherList,
        setVoucherList,
        currentVoucher,
        setChangeVoucher: handleChangeVoucher,
        listMilesPurchase,
        getPurchaseValues,
        milesToPurchase,
        setMilesToPurchase,
        fetchVoucherList,
        getListQuantityMilesToPurchase,
      }}
    >
      {children}
    </PurchaseContext.Provider>
  );
};

export default PurchaseProvider;
